import React, { useState, useEffect } from "react";
import { Chip, useTheme } from "@mui/material";

interface CellChipStatusProps {
  /** any value; will be converted to string */
  children: any;
}

const STATUS_COLOR_ALIASES: Record<string, string> = {
  SUCCESS: 'SUCCESS',
  COMPLETED: 'SUCCESS',

  RUNNING: 'RUNNING',
  ACTIVE: 'RUNNING',
  AVAILABLE: 'SUCCESS',
  UNDER_NURTURING: 'RUNNING',
  RESTARTING: 'PENDING',

  FAILED: 'FAILED',
  FAILURE: 'FAILED',
  INACTIVE: 'FAILED',
  UPSTREAM_FAILED: 'FAILED',

  PENDING: 'PENDING',
  WARNING: 'WARNING',
  WARN: 'WARNING',
  INITIALIZED_NURTURING_AUTOMATION: 'PENDING',
  SHUTDOWN: 'PENDING',
  DEFERRED: 'PENDING',
  QUEUED: 'PENDING',
  SCHEDULED: 'PENDING',
  UP_FOR_RESCHEDULE: 'PENDING',
  UP_FOR_RETRY: 'PENDING',

  SKIPPED: 'SKIPPED',
  ARCHIVED: 'NO_STATUS',
  NOT_UNDER_NURTURING: 'NO_STATUS',
  REMOVED: 'NO_STATUS',

  NOT_AVAILABLE: 'NO_STATUS',
  NO_STATUS: 'NO_STATUS',
};

const resolveStatusAlias = (key: string): string => {
  if (STATUS_COLOR_ALIASES[key]) {
    return STATUS_COLOR_ALIASES[key];
  }

  if (key.includes('UNDER_NURTURING')) {
    return 'RUNNING';
  }

  if (key.includes('AVAILABLE') && !key.includes('NOT_AVAILABLE')) {
    return 'SUCCESS';
  }

  return 'NO_STATUS';
};

const getCustomColors = (theme: any): Record<string, string> => {
  const statusChip = theme.palette.statusChip;

  return {
    SUCCESS: statusChip?.success ?? theme.palette.success.main,
    FAILED: statusChip?.failed ?? theme.palette.error.main,
    RUNNING: statusChip?.running ?? theme.palette.info.main,
    PENDING: statusChip?.pending ?? theme.palette.warning.main,
    WARNING: statusChip?.warning ?? theme.palette.warning.main,
    SKIPPED: statusChip?.skipped ?? theme.palette.secondary.main,
    NO_STATUS:
      statusChip?.noStatus ??
      (theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]),
  };
};

const formatLabel = (label: string) =>
  label
    .toLowerCase()
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const CellChipStatus: React.FC<CellChipStatusProps> = ({ children }) => {
  const theme = useTheme();
  const [runningText, setRunningText] = useState("Running");
  const noStatusTextColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[700];
  
  // convert any input to string
  const rawValue = String(children);
  // if string is empty or just whitespace, use fallback key
  const fallbackKey = 'NO_STATUS';
  const trimmed = rawValue.trim();
  const key = trimmed ? trimmed.toUpperCase().replace(/\s+/g, '_') : fallbackKey;
  const normalizedKey = resolveStatusAlias(key);

  const customColors = getCustomColors(theme);
  const backgroundColor = customColors[normalizedKey] || customColors.NO_STATUS;
  const formattedLabel = key === 'RUNNING' ? runningText : formatLabel(key);

  const greyTextStatuses = new Set(['NO_STATUS']);

  // Get text color based on background brightness and theme
  const getTextColor = () => {
    if (greyTextStatuses.has(normalizedKey)) {
      return noStatusTextColor;
    }
    // For colored backgrounds, use white text
    return theme.palette.common.white;
  };

  // Running text animation with setInterval
  useEffect(() => {
    if (normalizedKey === 'RUNNING' && key === 'RUNNING') {
      const runningStates = ["Running", "Running.", "Running..", "Running..."];
      let currentIndex = 0;
      
      const interval = setInterval(() => {
        currentIndex = (currentIndex + 1) % runningStates.length;
        setRunningText(runningStates[currentIndex]);
      }, 500); // Change every 500ms
      
      return () => clearInterval(interval);
    }
  }, [key, normalizedKey]);

  return (
    <Chip
      label={formattedLabel}
      size="small"
      sx={{
        backgroundColor,
        color: getTextColor(),
        fontWeight: 500,
        ...(normalizedKey === 'RUNNING' && key === 'RUNNING' && {
          color: `${theme.palette.common.white} !important`,
          minWidth: '85px',
          width: 'auto',
          justifyContent: 'flex-start',
          '& .MuiChip-label': {
            textAlign: 'left',
            justifyContent: 'flex-start',
            paddingLeft: '12px',
            paddingRight: '12px',
          }
        })
      }}
    />
  );
};

export default CellChipStatus;
